/**
 * Business Video/Images Upload Component
 * Allows owner to upload promotional video OR images (slideshow) for post-payment display
 */

import React, { useState, useEffect } from 'react';
import { Upload, Video, Trash2, CheckCircle, AlertCircle, Loader, Image as ImageIcon } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';

// Add rotating animation for loader
const styles = `
  @keyframes rotating {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  .rotating {
    animation: rotating 1s linear infinite;
  }
`;

const BusinessVideoUpload = ({ businessId, onClose }) => {
  const [mediaType, setMediaType] = useState('video'); // 'video' or 'images'
  const [video, setVideo] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [images, setImages] = useState([]); // Array of image files
  const [imageUrls, setImageUrls] = useState([]); // Array of image URLs from backend
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [previewUrl, setPreviewUrl] = useState(null);
  const [imagePreviewUrls, setImagePreviewUrls] = useState([]); // Array of preview URLs for selected images
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  // Fetch current video/images on mount
  useEffect(() => {
    if (businessId) {
      fetchBusinessMedia();
    }
  }, [businessId]);

  const fetchBusinessMedia = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
      const response = await fetch(`${API_URL}/api/business/${businessId}`);
      if (response.ok) {
        const data = await response.json();
        
        // Check for video
        if (data.video_url) {
          setVideoUrl(data.video_url);
          setMediaType('video');
        } 
        // Check for images
        else if (data.promo_images && Array.isArray(data.promo_images) && data.promo_images.length > 0) {
          setImageUrls(data.promo_images);
          setMediaType('images');
        }
      } else {
        console.error('❌ Failed to fetch business media, status:', response.status);
      }
    } catch (error) {
      console.error('❌ Error fetching business media:', error);
    }
  };

  const handleFileSelect = (e) => {
    const newFiles = Array.from(e.target.files);
    if (!newFiles || newFiles.length === 0) return;

    if (mediaType === 'video') {
      const file = newFiles[0];
      // Validate file type
      if (!file.type.startsWith('video/')) {
        setMessage({ type: 'error', text: 'Please select a video file' });
        return;
      }

      // Validate file size (max 100MB)
      const maxSize = 100 * 1024 * 1024;
      if (file.size > maxSize) {
        setMessage({ type: 'error', text: 'Video file must be less than 100MB' });
        return;
      }

      setVideo(file);
      setMessage({ type: '', text: '' });

      // Create preview URL
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    } else {
      // Handle multiple images - combine with existing
      const existingCount = images.length;
      const totalCount = existingCount + newFiles.length;
      
      if (totalCount > 5) {
        setMessage({ type: 'error', text: `Maximum 5 images allowed. You have ${existingCount} selected, can only add ${5 - existingCount} more.` });
        return;
      }

      // Validate all new files are images
      const invalidFiles = newFiles.filter(file => !file.type.startsWith('image/'));
      if (invalidFiles.length > 0) {
        setMessage({ type: 'error', text: 'Please select only image files' });
        return;
      }

      // Validate file sizes (max 10MB each)
      const maxSize = 10 * 1024 * 1024;
      const oversizedFiles = newFiles.filter(file => file.size > maxSize);
      if (oversizedFiles.length > 0) {
        setMessage({ type: 'error', text: 'Each image must be less than 10MB' });
        return;
      }

      // Combine existing and new images
      const combinedImages = [...images, ...newFiles];
      setImages(combinedImages);
      setMessage({ type: '', text: '' });

      // Create preview URLs for new files and combine with existing
      const newUrls = newFiles.map(file => URL.createObjectURL(file));
      const combinedUrls = [...imagePreviewUrls, ...newUrls];
      setImagePreviewUrls(combinedUrls);
    }
    
    // Reset file input to allow selecting the same file again
    e.target.value = '';
  };

  const handleUpload = async () => {
    if (mediaType === 'video' && !video) {
      setMessage({ type: 'error', text: 'Please select a video file first' });
      return;
    }
    
    if (mediaType === 'images' && images.length === 0) {
      setMessage({ type: 'error', text: 'Please select at least one image' });
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setMessage({ type: '', text: '' });
    setShowSuccessMessage(false);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
      
      // Use FormData to send files
      const formData = new FormData();
      
      if (mediaType === 'video') {
        formData.append('video', video);
      } else {
        // Append multiple images
        images.forEach((image, index) => {
          formData.append('images', image);
        });
      }
      
      // Create XMLHttpRequest for progress tracking
      const xhr = new XMLHttpRequest();
      
      // Track upload progress
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentComplete = Math.round((e.loaded / e.total) * 100);
          setUploadProgress(percentComplete);
        }
      });
      
      // Handle completion
      const uploadPromise = new Promise((resolve, reject) => {
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const data = JSON.parse(xhr.responseText);
              resolve(data);
            } catch (parseError) {
              reject(new Error('Invalid response from server'));
            }
          } else {
            try {
              const errorData = JSON.parse(xhr.responseText);
              reject(new Error(errorData.error || 'Upload failed'));
            } catch (parseError) {
              reject(new Error(`Upload failed with status ${xhr.status}`));
            }
          }
        });
        
        xhr.addEventListener('error', () => {
          reject(new Error('Network error occurred during upload'));
        });
        
        xhr.addEventListener('abort', () => {
          reject(new Error('Upload was cancelled'));
        });
      });
      
      // Send request to appropriate endpoint
      const endpoint = mediaType === 'video' 
        ? `${API_URL}/api/business/${businessId}/upload-video`
        : `${API_URL}/api/business/${businessId}/upload-images`;
      
      xhr.open('POST', endpoint);
      xhr.send(formData);
      
      // Wait for completion
      const data = await uploadPromise;
      
      setUploadProgress(100);
      
      if (mediaType === 'video') {
        setVideoUrl(data.videoUrl);
        setVideo(null);
        if (previewUrl) {
          URL.revokeObjectURL(previewUrl);
          setPreviewUrl(null);
        }
      } else {
        setImageUrls(data.imageUrls);
        setImages([]);
        imagePreviewUrls.forEach(url => URL.revokeObjectURL(url));
        setImagePreviewUrls([]);
      }
      
      setShowSuccessMessage(true);
      setMessage({ type: 'success', text: `${mediaType === 'video' ? 'Video' : 'Images'} uploaded successfully!` });
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setShowSuccessMessage(false);
        setUploadProgress(0);
      }, 3000);

    } catch (error) {
      console.error('Upload error:', error);
      setUploadProgress(0);
      setErrorMessage(error.message || `Failed to upload ${mediaType}. Please try again.`);
      setShowErrorModal(true);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    const confirmText = mediaType === 'video' ? 'this video' : 'these images';
    if (!window.confirm(`Are you sure you want to remove ${confirmText}?`)) {
      return;
    }

    setDeleting(true);
    setMessage({ type: '', text: '' });

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
      const endpoint = mediaType === 'video'
        ? `${API_URL}/api/business/${businessId}/video`
        : `${API_URL}/api/business/${businessId}/images`;
        
      const response = await fetch(endpoint, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Delete failed');
      }

      if (mediaType === 'video') {
        setVideoUrl(null);
      } else {
        setImageUrls([]);
      }
      
      setMessage({ type: 'success', text: `${mediaType === 'video' ? 'Video' : 'Images'} removed successfully!` });

    } catch (error) {
      console.error('Delete error:', error);
      setMessage({ type: 'error', text: error.message || `Failed to delete ${mediaType}` });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div style={{
      background: '#000000',
      borderRadius: '12px',
      padding: '24px',
      maxWidth: '600px',
      margin: '0 auto',
      position: 'relative'
    }}>
      {/* Inject styles */}
      <style>{styles}</style>
      
      {/* Close Button - Top Right */}
      {onClose && (
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            background: 'transparent',
            border: 'none',
            color: '#fff',
            cursor: 'pointer',
            fontSize: '20px',
            padding: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      )}
      
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '16px'
      }}>
        {mediaType === 'video' ? <Video size={24} color="#f59e0b" /> : <ImageIcon size={24} color="#f59e0b" />}
        <h3 style={{ color: '#fff', margin: 0 }}>Promotional {mediaType === 'video' ? 'Video' : 'Images'}</h3>
      </div>

      <p style={{ color: '#aaa', fontSize: '14px', marginBottom: '16px' }}>
        Upload a video or images slideshow that will be displayed to customers after they complete payment.
      </p>

      {/* Media Type Selection */}
      {!videoUrl && imageUrls.length === 0 && !video && images.length === 0 && (
        <div style={{
          display: 'flex',
          gap: '12px',
          marginBottom: '20px'
        }}>
          <button
            onClick={() => {
              setMediaType('video');
              setImages([]);
              setImagePreviewUrls([]);
            }}
            style={{
              flex: 1,
              padding: '10px',
              background: mediaType === 'video' ? '#f59e0b' : '#374151',
              color: '#fff',
              border: mediaType === 'video' ? '2px solid #f59e0b' : '2px solid #4B5563',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'all 0.2s'
            }}
          >
            <Video size={18} />
            Video
          </button>
          <button
            onClick={() => {
              setMediaType('images');
              setVideo(null);
              setPreviewUrl(null);
            }}
            style={{
              flex: 1,
              padding: '10px',
              background: mediaType === 'images' ? '#f59e0b' : '#374151',
              color: '#fff',
              border: mediaType === 'images' ? '2px solid #f59e0b' : '2px solid #4B5563',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'all 0.2s'
            }}
          >
            <ImageIcon size={18} />
            Images
          </button>
        </div>
      )}

      {/* Current Video Display */}
      {videoUrl && (
        <div style={{
          background: '#0a0a0a',
          border: '1px solid #333',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '16px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '12px',
            position: 'relative',
            minHeight: '32px'
          }}>
            <div style={{ flex: 1 }} />
            <button
              onClick={handleDelete}
              disabled={deleting}
              style={{
                background: 'transparent',
                color: '#ff4444',
                border: 'none',
                borderRadius: '50%',
                padding: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: deleting ? 'not-allowed' : 'pointer',
                opacity: deleting ? 0.5 : 1,
                fontSize: '18px',
                marginLeft: 'auto',
              }}
              title="Delete video"
            >
              {deleting ? <Loader size={16} /> : <Trash2 size={20} />}
            </button>
          </div>
          <video
            src={videoUrl}
            controls
            style={{
              width: '100%',
              maxHeight: '80px',
              borderRadius: '8px',
              background: '#000'
            }}
          />
        </div>
      )}

      {/* Current Images Display */}
      {imageUrls.length > 0 && images.length === 0 && (
        <div style={{
          background: 'rgb(24, 24, 24)',
          border: '1px solid #333',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '16px'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '12px'
          }}>
            <span style={{ color: '#fff', fontSize: '14px', fontWeight: '600' }}>
              {imageUrls.length} Image{imageUrls.length > 1 ? 's' : ''}
            </span>
            <button
              onClick={handleDelete}
              disabled={deleting}
              style={{
                background: 'transparent',
                color: '#ff4444',
                border: 'none',
                borderRadius: '50%',
                padding: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: deleting ? 'not-allowed' : 'pointer',
                opacity: deleting ? 0.5 : 1,
                fontSize: '18px'
              }}
              title="Delete images"
            >
              {deleting ? <Loader size={16} /> : <Trash2 size={20} />}
            </button>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${Math.min(imageUrls.length, 3)}, 1fr)`,
            gap: '8px'
          }}>
            {imageUrls.map((url, index) => (
              <img
                key={index}
                src={url}
                alt={`Promo ${index + 1}`}
                style={{
                  width: '100%',
                  height: '80px',
                  objectFit: 'cover',
                  borderRadius: '8px',
                  border: 'none'
                }}
              />
            ))}
          </div>
        </div>
      )}

      {/* Upload Section */}
      <div>
        <input
          type="file"
          accept={mediaType === 'video' ? 'video/*' : 'image/*'}
          onChange={handleFileSelect}
          id="media-upload"
          multiple={mediaType === 'images'}
          style={{ display: 'none' }}
          disabled={uploading}
        />
        
        {video && previewUrl && (
          <div style={{
            background: 'rgb(24, 24, 24)',
            border: '1px solid #333',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '16px'
          }}>
            <p style={{ color: '#fff', fontSize: '14px', marginBottom: '12px', marginTop: '0px' }}>
              Selected: <strong>{video.name}</strong> ({(video.size / 1024 / 1024).toFixed(2)} MB)
            </p>
            <video
              src={previewUrl}
              controls
              style={{
                width: '100%',
                maxHeight: '80px',
                borderRadius: '8px',
                background: '#000',
                marginBottom: '16px'
              }}
            />
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={() => {
                  if (previewUrl) {
                    URL.revokeObjectURL(previewUrl);
                  }
                  setVideo(null);
                  setPreviewUrl(null);
                  setMessage({ type: '', text: '' });
                }}
                disabled={uploading}
                style={{
                  flex: 1,
                  background: 'transparent',
                  color: '#fff',
                  border: '2px solid #6b7280',
                  borderRadius: '8px',
                  padding: '12px 24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  cursor: uploading ? 'not-allowed' : 'pointer',
                  opacity: uploading ? 0.5 : 1,
                  fontSize: '16px',
                  fontWeight: '600',
                  transition: 'all 0.2s'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={uploading}
                style={{
                  flex: 1,
                  background: '#f59e0b',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px 24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  cursor: uploading ? 'not-allowed' : 'pointer',
                  opacity: uploading ? 0.5 : 1,
                  fontSize: '16px',
                  fontWeight: '600',
                  transition: 'all 0.2s'
                }}
              >
                {uploading && <Loader size={20} className="rotating" />}
                Save
              </button>
            </div>
          </div>
        )}

        {images.length > 0 && imagePreviewUrls.length > 0 && (
          <div style={{
            background: 'rgb(24, 24, 24)',
            border: '1px solid #333',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '16px'
          }}>
            <p style={{ color: '#fff', fontSize: '14px', marginBottom: '12px' }}>
              Selected: <strong>{images.length} image{images.length > 1 ? 's' : ''}</strong>
            </p>
            <div style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${Math.min(images.length, 3)}, 1fr)`,
              gap: '8px',
              marginBottom: '16px'
            }}>
              {imagePreviewUrls.map((url, index) => (
                <div key={index} style={{ position: 'relative' }}>
                  <img
                    src={url}
                    alt={`Preview ${index + 1}`}
                    style={{
                      width: '100%',
                      height: '80px',
                      objectFit: 'cover',
                      borderRadius: '8px',
                      border: '2px solid #f59e0b'
                    }}
                  />
                  <div style={{
                    position: 'absolute',
                    top: '4px',
                    left: '4px',
                    background: 'rgba(0,0,0,0.8)',
                    color: '#fff',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}>
                    {index + 1}
                  </div>
                  <button
                    onClick={() => {
                      // Remove specific image
                      URL.revokeObjectURL(url);
                      const newImages = images.filter((_, i) => i !== index);
                      const newUrls = imagePreviewUrls.filter((_, i) => i !== index);
                      setImages(newImages);
                      setImagePreviewUrls(newUrls);
                      if (newImages.length === 0) {
                        setMessage({ type: '', text: '' });
                      }
                    }}
                    style={{
                      position: 'absolute',
                      top: '4px',
                      right: '4px',
                      background: '#ff4444',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '50%',
                      width: '24px',
                      height: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      lineHeight: '1'
                    }}
                    title="Remove this image"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={() => {
                  imagePreviewUrls.forEach(url => URL.revokeObjectURL(url));
                  setImages([]);
                  setImagePreviewUrls([]);
                  setMessage({ type: '', text: '' });
                }}
                disabled={uploading}
                style={{
                  flex: 1,
                  background: 'transparent',
                  color: '#fff',
                  border: '2px solid #6b7280',
                  borderRadius: '8px',
                  padding: '12px 24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  cursor: uploading ? 'not-allowed' : 'pointer',
                  opacity: uploading ? 0.5 : 1,
                  fontSize: '16px',
                  fontWeight: '600',
                  transition: 'all 0.2s'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={uploading}
                style={{
                  flex: 1,
                  background: '#f59e0b',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px 24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  cursor: uploading ? 'not-allowed' : 'pointer',
                  opacity: uploading ? 0.5 : 1,
                  fontSize: '16px',
                  fontWeight: '600',
                  transition: 'all 0.2s'
                }}
              >
                {uploading && <Loader size={20} className="rotating" />}
                Save
              </button>
            </div>
          </div>
        )}

        {!video && images.length === 0 && (
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>
            <label
              htmlFor="media-upload"
              style={{
                background: '#0a0a0a',
                color: '#f59e0b',
                border: '2px solid #f59e0b',
                borderRadius: '8px',
                padding: '12px 24px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                cursor: uploading ? 'not-allowed' : 'pointer',
                opacity: uploading ? 0.5 : 1,
                fontSize: '16px',
                fontWeight: '600',
                transition: 'all 0.2s'
              }}
            >
              <Upload size={20} />
              Choose {mediaType === 'video' ? 'Video' : 'Images'}
            </label>
          </div>
        )}
      </div>

      {/* Upload Progress Bar */}
      {uploading && uploadProgress > 0 && (
        <div style={{
          marginTop: '16px',
          background: '#0a0a0a',
          border: '1px solid #333',
          borderRadius: '8px',
          padding: '16px'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '8px'
          }}>
            <span style={{ color: '#fff', fontSize: '14px', fontWeight: '600' }}>
              Uploading...
            </span>
            <span style={{ color: '#22C55E', fontSize: '14px', fontWeight: '600' }}>
              {uploadProgress}%
            </span>
          </div>
          <div style={{
            width: '100%',
            height: '6px',
            background: '#1F2937',
            borderRadius: '3px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${uploadProgress}%`,
              height: '100%',
              background: 'linear-gradient(90deg, #22C55E, #10B981)',
              borderRadius: '3px',
              transition: 'width 0.3s ease',
              boxShadow: '0 0 10px rgba(34, 197, 94, 0.5)'
            }} />
          </div>
        </div>
      )}

      {/* Message Display - Only for non-upload errors */}
      {message.text && !uploading && !showSuccessMessage && (
        <div style={{
          marginTop: '16px',
          padding: '12px 16px',
          borderRadius: '8px',
          background: message.type === 'success' ? '#1a3a1a' : '#3a1a1a',
          border: `1px solid ${message.type === 'success' ? '#22C55E' : '#ff4444'}`,
          color: message.type === 'success' ? '#22C55E' : '#ff4444',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '14px'
        }}>
          {message.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
          {message.text}
        </div>
      )}

      {/* Instructions */}
      <div style={{
        marginTop: '20px',
        padding: '16px',
        background: 'rgb(24, 24, 24)',
        border: '1px solid #333',
        borderRadius: '8px',
        fontSize: '13px',
        color: '#aaa',
        textAlign: 'left'
      }}>
        <p style={{ margin: '0 0 8px 0', fontWeight: '600', color: '#fff', textAlign: 'left' }}>
          {mediaType === 'video' ? 'Video' : 'Images'} Guidelines:
        </p>
        <ul style={{ margin: 0, paddingLeft: '20px', textAlign: 'left' }}>
          {mediaType === 'video' ? (
            <>
              <li style={{ textAlign: 'left' }}>Maximum file size: 100MB</li>
              <li style={{ textAlign: 'left' }}>Supported formats: MP4, WebM, OGG</li>
              <li style={{ textAlign: 'left' }}>Recommended duration: 15-60 seconds</li>
              <li style={{ textAlign: 'left' }}>Video will loop automatically for customers</li>
            </>
          ) : (
            <>
              <li style={{ textAlign: 'left' }}>Upload 1-5 images for slideshow</li>
              <li style={{ textAlign: 'left' }}>Maximum file size per image: 10MB</li>
              <li style={{ textAlign: 'left' }}>Supported formats: JPG, PNG, WebP</li>
              <li style={{ textAlign: 'left' }}>Each image displays for 3 seconds</li>
              <li style={{ textAlign: 'left' }}>Slideshow loops automatically for customers</li>
            </>
          )}
        </ul>
      </div>

      {/* Error Modal - Only shows on upload errors */}
      {showErrorModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            background: '#000000',
            borderRadius: '12px',
            padding: '30px',
            maxWidth: '450px',
            width: '90%',
            boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
            border: '1px solid #6b7280'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '20px'
            }}>
              <div style={{
                width: '70px',
                height: '70px',
                borderRadius: '50%',
                background: 'rgba(255, 68, 68, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <AlertCircle size={36} color="#ff4444" strokeWidth={2.5} />
              </div>
            </div>
            <h3 style={{
              margin: '0 0 12px 0',
              color: '#fff',
              fontSize: '22px',
              fontWeight: '700',
              textAlign: 'center'
            }}>
              Upload Failed
            </h3>
            <p style={{
              margin: '0 0 25px 0',
              color: '#ccc',
              fontSize: '15px',
              textAlign: 'center',
              lineHeight: '1.6',
              padding: '0 10px'
            }}>
              {errorMessage}
            </p>
            <button
              onClick={() => {
                setShowErrorModal(false);
                setErrorMessage('');
              }}
              style={{
                width: '100%',
                background: '#ff4444',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                padding: '14px',
                fontSize: '16px',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.background = '#ff6666'}
              onMouseLeave={(e) => e.target.style.background = '#ff4444'}
            >
              Try Again
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessVideoUpload;
